/**
 * Describing attacker-influenceable content in a log line without reproducing it.
 *
 * Translation values arrive from the server and are influenceable by whoever
 * can write a translation. Interpolating one whole into a log line on a path
 * that runs PER RENDER turns a single poisoned row into unbounded, repeating
 * log volume — the same amplification found in @lionrapid/formatters, where a
 * 40k-marker template wrote ~320KB per call.
 *
 * A bare truncation would bound the volume and destroy the point of the line,
 * so this keeps what actually answers "which translation, and what was in it":
 *
 *   LENGTH       — the real length, not the truncated one, so a pathological
 *                  value is visibly pathological.
 *   FINGERPRINT  — md5 truncated to 12, the SAME function and width used to
 *                  derive `auto_` keys (`generateAutoKey` = `auto_` + md5 of
 *                  the TRIMMED text, first 12). For a markup-free source string
 *                  the value printed here is exactly the key's hash, so the row
 *                  can be looked up by prefixing `auto_`. For a value carrying
 *                  markup the auto key is derived from the parsed ContentUnit's
 *                  text rather than the raw string, so it is a stable
 *                  fingerprint but NOT the key — hence `md5:` rather than
 *                  `auto_`, which would overclaim.
 *   EXCERPT      — head and tail, quoted and escaped.
 *
 * Escaping is not cosmetic. The content can contain newlines, and a log line
 * assembled by interpolation is forgeable: a value containing `\n   Error: ...`
 * writes what looks like its own log entry. JSON.stringify escapes it back into
 * a single quoted token.
 *
 * @module utils/log-safe
 */

import { md5 } from '../browser/md5.js';

/** At or below this, the value is short enough to print whole — nothing lost. */
const LOG_VALUE_FULL_MAX = 200;

/** Characters kept at each end of a value too long to print whole. */
const LOG_VALUE_EDGE = 90;

/**
 * Identity of a value without reproducing it: `"1234 chars, md5:ab12cd34ef56"`.
 *
 * Hashes the TRIMMED value, matching `generateAutoKey`, so the two agree
 * wherever the auto key was derived from this exact string.
 */
export function contentIdentity(value: string): string {
  return `${value.length} chars, md5:${md5(value.trim()).slice(0, 12)}`;
}

/**
 * A bounded, quoted, escaped excerpt.
 *
 * Head AND tail rather than a prefix: these failures carry no offset to centre
 * a window on (unlike an ICU SyntaxError, which reports one), and the end of a
 * value is where an unterminated tag or a truncated payload shows itself.
 */
export function contentExcerpt(value: string): string {
  if (value.length <= LOG_VALUE_FULL_MAX) {
    return JSON.stringify(value);
  }
  return JSON.stringify(
    `${value.slice(0, LOG_VALUE_EDGE)}…${value.slice(-LOG_VALUE_EDGE)}`
  );
}

/** Bound on a rendered error message. */
const LOG_ERROR_MAX = 200;

/** Bound on rendered non-error log data. */
const LOG_DATA_MAX = 500;

/** Stack frames kept when an error is rendered as a log's data argument. */
const LOG_STACK_FRAMES = 2;

/** Bound on a single rendered stack frame. */
const LOG_FRAME_MAX = 120;

/** Truncate to `max`, saying how much was dropped rather than dropping silently. */
function bound(text: string, max: number): string {
  return text.length <= max
    ? text
    : `${text.slice(0, max)}… (${text.length} chars)`;
}

/**
 * Collapse to a single escaped line.
 *
 * `JSON.stringify` of a string quotes and escapes it, and `.slice(1, -1)`
 * removes the quotes it adds — the goal is escaping, not quoting, because this
 * gets embedded in a sentence. Escaping matters because error messages are not
 * reliably content-free: intl-messageformat's MissingValueError carries the
 * template it failed on, so a translation containing newlines could otherwise
 * forge log lines through the error message.
 */
function oneLine(text: string): string {
  return JSON.stringify(text).slice(1, -1);
}

/**
 * A thrown value, as one bounded, escaped line: `"TypeError: boom"`.
 *
 * INCLUDES THE NAME. It previously returned `error.message` for Errors and
 * `String(value)` for everything else, which was backwards: `String(err)` keeps
 * the name ("TypeError: boom") while `.message` drops it ("boom"), so a
 * non-Error carried MORE identifying information than an Error did. The name is
 * the distinction that separates "bad template" (MissingValueError) from "our
 * bug" (TypeError), and neither line carried it.
 *
 * Bounded because an error message is not automatically small OR content-free —
 * see `oneLine`. No stack: this is used INLINE, mid-sentence, where frames read
 * badly. `describeLogData` adds them where they land at the end of a line.
 */
export function describeError(error: unknown): string {
  if (error instanceof Error) {
    const name = error.name || 'Error';
    return bound(oneLine(`${name}: ${error.message}`), LOG_ERROR_MAX);
  }
  return bound(oneLine(String(error)), LOG_ERROR_MAX);
}

/**
 * Anything handed to a logger as its `data` argument, rendered safely.
 *
 * The logger used to do `JSON.stringify(data)` in both of its rendering
 * branches. That was wrong three ways, and every one of them was silent:
 *
 *   ERRORS VANISHED   — `JSON.stringify(new Error('boom'))` is `{}`, because an
 *                       Error's fields are not enumerable. Both branches end by
 *                       building a single string for `console.*`, so devtools
 *                       could not expand the object either. Every caught error
 *                       passed as data — across the whole package — printed
 *                       `{}` and always had.
 *   UNBOUNDED         — any object rendered whole, so a large payload was log
 *                       amplification on a path that runs per render.
 *   IT COULD THROW    — `JSON.stringify` throws on a circular structure or a
 *                       BigInt, so logging inside a `catch` could replace the
 *                       error being diagnosed with a TypeError from the logger.
 *
 * Errors get name, bounded message and the first couple of frames, joined with
 * ` | ` rather than newlines so the whole record stays one greppable line and a
 * forged message cannot masquerade as a frame.
 */
export function describeLogData(data: unknown): string {
  if (data instanceof Error) {
    // `stack` begins with "Name: message", and a message can be MULTI-LINE —
    // so skipping one line is not enough. A message containing newlines would
    // otherwise have its own lines read as stack frames, letting an
    // attacker-influenceable message forge frames even though the message
    // itself is escaped. Skip exactly as many lines as the message occupies,
    // then keep only things that actually look like frames.
    const headerLines = String(data.message ?? '').split('\n').length;
    const frames = (data.stack ?? '')
      .split('\n')
      .slice(headerLines)
      .map((frame) => frame.trim())
      .filter((frame) => frame.startsWith('at '))
      .slice(0, LOG_STACK_FRAMES)
      .map((frame) => bound(frame, LOG_FRAME_MAX));
    return [describeError(data), ...frames].join(' | ');
  }

  let rendered: string | undefined;
  try {
    rendered = JSON.stringify(data);
  } catch {
    // Circular, BigInt, or a throwing toJSON. The logger must never be the
    // thing that fails.
    return '[unserializable]';
  }
  // `JSON.stringify` returns undefined for functions and symbols.
  return bound(rendered ?? oneLine(String(data)), LOG_DATA_MAX);
}

/**
 * Identity plus excerpt, ready to drop into a log line.
 *
 * `1234 chars, md5:ab12cd34ef56, "the start…the end"`
 */
export function describeContent(value: string): string {
  return `${contentIdentity(value)}, ${contentExcerpt(value)}`;
}
